import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  FilterBar,
  FilterSelect,
  FilterSearch,
} from '@/components/ui/page-toolbar'
import { useFinancePagamentosPage } from '@/hooks/useFinancePagamentosPage'
import { FinancePagamentosTable } from '@/components/Financeiro/FinancePagamentosTable'
import { FinanceNfDetailDrawer } from '@/components/Financeiro/FinanceNfDetailDrawer'
import {
  formatCompetenciaLabel,
  PJ_NF_STATUS_LABELS,
  shiftCompetencia,
} from '@/components/PjNf/pjNfStatus'
import type { PjNfMensalStatus } from '@/services/pjNfMensalService'

const STATUS_OPTIONS: Array<{ value: PjNfMensalStatus | ''; label: string }> = [
  { value: '', label: 'Todos os status' },
  ...(
    Object.entries(PJ_NF_STATUS_LABELS) as Array<[PjNfMensalStatus, string]>
  ).map(([value, label]) => ({ value, label })),
]

function StatCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className='glass-card px-4 py-3'>
      <p className='text-xs uppercase text-[var(--font-color-label)]'>{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${tone ?? 'text-[var(--font-color-h2)]'}`}>
        {value}
      </p>
    </div>
  )
}

export function FinancePagamentosSection() {
  const {
    competencia,
    setCompetencia,
    rows,
    summary,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    selected,
    detailLoading,
    openDetail,
    closeDetail,
    marcarEmValidacao,
    validar,
    pagar,
  } = useFinancePagamentosPage()

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            className='rounded-lg border border-[var(--glass-border)] p-2 hover:bg-[var(--glass-bg)]'
            onClick={() => setCompetencia(shiftCompetencia(competencia, -1))}
            aria-label='Mês anterior'
          >
            <ChevronLeft size={16} />
          </button>
          <span className='min-w-[140px] text-center text-sm font-semibold text-[var(--font-color-h2)]'>
            {formatCompetenciaLabel(competencia)}
          </span>
          <button
            type='button'
            className='rounded-lg border border-[var(--glass-border)] p-2 hover:bg-[var(--glass-bg)]'
            onClick={() => setCompetencia(shiftCompetencia(competencia, 1))}
            aria-label='Próximo mês'
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <FilterBar>
          <FilterSearch
            value={search}
            onChange={setSearch}
            placeholder='Buscar prestador…'
          />
          <FilterSelect
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as PjNfMensalStatus | '')}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </FilterSelect>
        </FilterBar>
      </div>

      {error && (
        <div className='rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger'>
          {error}
        </div>
      )}

      {summary && (
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-6'>
          <StatCard label='Aguardando' value={summary.aguardando} tone='text-yellow-400' />
          <StatCard label='Enviadas' value={summary.enviadas} tone='text-blue-400' />
          <StatCard label='Em validação' value={summary.emValidacao} tone='text-violet-400' />
          <StatCard label='Aprovadas' value={summary.aprovadas} tone='text-emerald-400' />
          <StatCard label='Rejeitadas' value={summary.rejeitadas} tone='text-red-400' />
          <StatCard label='Pagas' value={summary.pagas} tone='text-emerald-300' />
        </div>
      )}

      <FinancePagamentosTable rows={rows} loading={loading} onSelect={openDetail} />

      <FinanceNfDetailDrawer
        open={Boolean(selected)}
        loading={detailLoading}
        nf={selected}
        onClose={closeDetail}
        onMarcarEmValidacao={marcarEmValidacao}
        onValidar={validar}
        onPagar={pagar}
      />
    </div>
  )
}
